import styled, { css, DefaultTheme } from 'styled-components';
import { TButtonShape, TButtonSize, TButtonSurface, TButtonVariant } from './icon-button.types';
import { buttonSize, buttonTextConfig, overlayConfig } from './icon-button.utils';
import { rgba } from 'polished';

const clsPrefix = 'ui-icon-button';

export const iconButtonClasses = {
  wrapper: clsPrefix,
  inner: `${clsPrefix}-inner`,
  label: `${clsPrefix}-label`,
  icon: `${clsPrefix}-icon`,
  loading: `${clsPrefix}-loading`,
  loader: `${clsPrefix}-loader`,
} as const;

function getBorderRadius(theme: DefaultTheme, size: TButtonSize, shape: TButtonShape) {
  if (shape === 'round') {
    return '50%';
  }

  switch (size) {
    case 'extra-small':
      return `${theme.radius.extraSmall}px`;
    case 'small':
    case 'medium':
      return `${theme.radius.small}px`;
    case 'large':
      return `${theme.radius.medium}px`;
    case 'extra-large':
      return `${theme.radius.large}px`;
    default:
      return `0px`;
  }
}

export const ButtonContentSC = styled.div<{ $loading?: boolean }>`
  flex: 1;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;

  visibility: ${({ $loading }) => ($loading ? 'hidden' : 'visible')};
`;

export const IconButtonSC = styled.button<{
  $variant: TButtonVariant;
  $size: TButtonSize;
  $shape: TButtonShape;
  $color: string;
  $backgroundColor: string;
  $borderColor: string | null;
  $surface: TButtonSurface;
}>`
  position: relative;
  background: none;
  border: none;
  outline: none;
  padding: 0;
  margin: 0;

  &,
  .${iconButtonClasses.inner}, .${iconButtonClasses.loader} {
    transition: all 0.2s ease-in-out;
  }

  &:not(:disabled) {
    cursor: pointer;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }

  .${iconButtonClasses.inner} {
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: ${({ $size, theme }) => ($size === 'extra-large' ? theme.shadows.low : '0')};

    .${iconButtonClasses.loader} {
      position: absolute;
      inset: 0;

      display: flex;
      align-items: center;
      justify-content: center;
    }
  }

  &.${iconButtonClasses.loading} {
    ${iconButtonClasses.inner} {
      opacity: 0;
    }
  }

  ${({ theme, $size, $color, $backgroundColor, $variant, $borderColor, $shape, $surface }) => {
    const dim = buttonSize[$size];
    const typo = buttonTextConfig[$size];

    return css`
      .${iconButtonClasses.inner}, .${iconButtonClasses.loader} {
        width: ${dim}px;
        height: ${dim}px;
        border: ${$borderColor ? `2px solid ${$borderColor}` : 'none'};
        border-radius: ${getBorderRadius(theme, $size, $shape)};
        background-color: ${$backgroundColor};
      }

      .${iconButtonClasses.label} {
        margin-top: 4px;
        color: ${$color};
        ${theme.mappedTypography[typo.display][typo.variant]};
      }

      &:hover:not(:disabled) {
        outline: none;
        .${iconButtonClasses.inner}, .${iconButtonClasses.loader} {
          background-color: ${theme.createOverlayColor(
            $backgroundColor,
            'hover',
            overlayConfig[$surface][$variant],
          )};
          box-shadow: ${$variant === 'primary'
            ? `0px 0px 4px 2px ${rgba(theme.color.solid.selection, 0.5)};`
            : '0'};
        }

        .${iconButtonClasses.label} {
          color: ${theme.createOverlayColor($color, 'hover', overlayConfig[$surface][$variant])};
        }
      }

      &:active {
        outline: none;
        .${iconButtonClasses.inner}, .${iconButtonClasses.loader} {
          opacity: 0.2;
        }
      }

      &:focus {
        outline: none;
        .${iconButtonClasses.inner}, .${iconButtonClasses.loader} {
          background-color: ${theme.createOverlayColor(
            $backgroundColor,
            'focused',
            overlayConfig[$surface][$variant],
          )};
        }
      }
    `;
  }};
`;
